/**
 * CommBank (CBA) CSV Parser (pure, browser-safe).
 *
 * CBA's "CSV (simple)" transaction export is HEADERLESS with four
 * fully-quoted columns (except the date):
 *
 *   Date, Amount, Description, Balance
 *
 * e.g.  19/06/2026,"+336.28","Direct Credit 015884 AUS GOV FAMILIES ...","+433.75"
 *
 * Quirks handled:
 *   - Date is DD/MM/YYYY (Australian), unquoted.
 *   - Amount carries an EXPLICIT sign ("+336.28" / "-1227.22") and is quoted;
 *     positive = money in, negative = money out.
 *   - Description is quoted (so it may legitimately contain commas).
 *   - A 4th running-Balance column we don't need for import (dropped).
 *
 * We only surface the unified {date, amount, description} shape the importer
 * shares with the ANZ/Coles parsers; the balance is ignored.
 */

export interface ParsedCbaRow {
  /** ISO YYYY-MM-DD. */
  date: string;
  /** Signed AUD amount. Negative = money out. */
  amount: number;
  /** Description column, whitespace-normalised. */
  description: string;
}

export interface CbaParseResult {
  rows: ParsedCbaRow[];
  errors: Array<{ line: number; text: string; reason: string }>;
}

/** Quote-aware CSV line splitter (handles "a, b",c and "" escapes). */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

/** Convert "19/06/2026" → "2026-06-19". Returns null if invalid. */
function parseCbaDate(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

/** Parse "+336.28" / "-1227.22" / "0.00" → number. Number() handles the
 *  leading '+'. Returns null on anything unparseable. */
function parseCbaAmount(s: string): number | null {
  const v = s.trim().replace(/,/g, '');
  if (!/^[+-]?\d+(\.\d+)?$/.test(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Sniff: does this text look like a CBA export? First non-empty line must be
 *  4 columns: a DD/MM/YYYY date, a signed amount, a description, and a signed
 *  balance. The numeric-balance check disambiguates from ANZ (3 columns whose
 *  unquoted description can contain commas). */
export function looksLikeCbaCsv(text: string): boolean {
  const first = text.split(/\r?\n/).find((l) => l.trim()) || '';
  const cols = splitCsvLine(first);
  if (cols.length < 4) return false;
  const dateOk = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cols[0].trim());
  const amtOk = /^[+-]?\d+(\.\d+)?$/.test(cols[1].trim());
  const balOk = /^[+-]?\d+(\.\d+)?$/.test(cols[3].trim());
  return dateOk && amtOk && balOk;
}

/** Parse a full CBA CSV (as text). Tolerates blank lines. File order preserved. */
export function parseCbaCsv(text: string): CbaParseResult {
  const rows: ParsedCbaRow[] = [];
  const errors: Array<{ line: number; text: string; reason: string }> = [];
  const lines = text.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) continue;

    const cols = splitCsvLine(line);
    if (cols.length < 3) {
      errors.push({ line: i + 1, text: raw, reason: `Expected 4 columns (date, amount, description, balance), got ${cols.length}` });
      continue;
    }
    const date = parseCbaDate(cols[0]);
    if (!date) {
      errors.push({ line: i + 1, text: raw, reason: `Invalid date '${cols[0]}' (expected DD/MM/YYYY)` });
      continue;
    }
    const amount = parseCbaAmount(cols[1]);
    if (amount === null) {
      errors.push({ line: i + 1, text: raw, reason: `Invalid amount '${cols[1]}'` });
      continue;
    }
    const description = String(cols[2] || '').trim().replace(/\s+/g, ' ');
    if (!description) {
      errors.push({ line: i + 1, text: raw, reason: 'Empty description' });
      continue;
    }
    rows.push({ date, amount, description });
  }

  return { rows, errors };
}
